let map: google.maps.Map
export async function initMap(mapEl: HTMLElement) {
    if (!mapEl || mapEl.hasAttribute('data-initialized')) return

    mapEl.setAttribute('data-initialized', '')
    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary,
        { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary;

    const type = mapEl.dataset.map ?? "single",
        themeRoot = mapEl.dataset.themeUri,
        mapOptions: google.maps.MapOptions = {
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            styles: mapStyle
        }

    if (type === 'single') {
        let location: google.maps.LatLng = null;

        const address = mapEl.dataset.address ?? null
        if (address) {
            const geocoder = new Geocoder();

            await geocoder.geocode({ address }, (results, status) => {
                if (status !== "OK") return
                location = results[0]?.geometry.location ?? null
            })
        } else {
            const lat = mapEl.dataset.lat ?? null,
                lng = mapEl.dataset.lng ?? null

            if (lat && lng) {
                location = new google.maps.LatLng(parseFloat(lat), parseFloat(lng))
            }
        }

        if (!location) return;

        mapOptions.disableDefaultUI = true
        mapOptions.center = location
        mapOptions.zoom = parseInt(mapEl.dataset.zoom ?? '13')
    } else if (type === 'salons') {
        mapOptions.center = { lat: 50.0596405, lng: 14.3832248 }
        mapOptions.zoom = 11
    }

    let markers = [];
    map = new Map(mapEl, mapOptions);

    if (type === 'single') {
        new google.maps.Marker({
            position: mapOptions.center,
            clickable: false,
            icon: {
                url: `${themeRoot}/resources/icons/pin.svg`,
                scaledSize: new google.maps.Size(35, 35),
            },
            map: map
        });
    } else if (type === 'salons') {
        const bounds = new google.maps.LatLngBounds(),
            isArchiveMap = document.body.classList.contains('archive'),
            salons = typeof salonsData !== 'undefined' ? salonsData
                : isArchiveMap ? Livewire.first().posts : []

        salons.forEach(salon => {
            if (!salon.lat.length || !salon.lng.length) return

            const marker = createSalonMarker(salon, isArchiveMap)
            markers.push(marker)
            bounds.extend(marker.getPosition())
        })

        if (salons.length > 1) {
            map.fitBounds(bounds, 80)
        } else if (salons.length) {
            map.setCenter(getSalonLocation(salons[0]))
            map.setZoom(14)
        }
    }

    map.addListener("tilesloaded", () => mapEl.classList.add('is-loaded'))

    const insertBefore = document.head.insertBefore
    document.head.insertBefore = function(newElement: Node, referenceElement: Node) {
        const newLinkElement = newElement as HTMLLinkElement;
        if (newLinkElement.href && newLinkElement.href.indexOf("//fonts.googleapis.com/") > -1) return null;

        insertBefore.call(document.head, newElement, referenceElement);
    }

    if (!document.body.classList.contains('archive')) return;

    document.querySelector('[data-location-find-me]')?.addEventListener("click", () => {
        navigator.geolocation.getCurrentPosition(async (position) => {
            await new Geocoder().geocode({
                'location': new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
                'region': "cz"
            }).then((response) => {
                const locality = response.results?.find(response =>
                    response.types.includes('locality'))?.formatted_address ?? ''

                window.dispatchEvent(new CustomEvent("setLocationSearchQuery", { detail: { locality } }))
                Livewire.first().$call('searchByLocation', [position.coords.latitude, position.coords.longitude])

                localStorage.setItem(`${position.coords.latitude},${position.coords.longitude}`, locality)
            }).catch((error) => {
                console.log(error);
            });
        });
    });

    const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

    let predictions: google.maps.places.PlacePrediction[] = [];
    let requestBody = {
        input: "",
        includedRegionCodes: ["cz"],
        includedPrimaryTypes: [
            "geocode"
        ],
        language: "cs-CZ",
        region: "cz"
    }

    //@ts-ignore
    requestBody.sessionToken = new AutocompleteSessionToken();

    window.addEventListener("getLocationSuggestions", async (e: CustomEvent) => {
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            ...requestBody,
            input: e.detail.search ?? ''
        });

        predictions = suggestions.map(suggestion => suggestion.placePrediction)
        window.dispatchEvent(new CustomEvent("handleLocationSuggestions", { detail: {
            suggestions: predictions.map(prediction => prediction!.text.toString())
        }}))
    })

    window.addEventListener("selectLocationSuggestions", async (e: CustomEvent) => {
        const place = predictions[e.detail.placePrediction]?.toPlace()
        await place.fetchFields({
            fields: ['location']
        })

        Livewire.first().$call('searchByLocation', [place.location.lat(), place.location.lng()])
        localStorage.setItem(`${place.location.lat()},${place.location.lng()}`, predictions[e.detail.placePrediction]!.text.toString())

        //@ts-ignore
        requestBody.sessionToken = new AutocompleteSessionToken();
    })

    const dispatchMapChange = () => {
        if(!isMapBeyondFirstRender(mapEl)) return

        const mapBounds = map.getBounds(),
            bounds = [
                mapBounds.getSouthWest().lat(),
                mapBounds.getSouthWest().lng(),
                mapBounds.getNorthEast().lat(),
                mapBounds.getNorthEast().lng()
            ]

        Livewire.first().$call('searchByMap', bounds)
    }

    let dragEvent, zoomEvent;

    const addUserMapListeners = () => {
        dragEvent = map.addListener("dragend", dispatchMapChange)
        zoomEvent = map.addListener("zoom_changed", dispatchMapChange)
    }

    const removeUserMapListeners = () => {
        google.maps.event.removeListener(dragEvent);
        google.maps.event.removeListener(zoomEvent);
    }

    addUserMapListeners();

    Livewire.on('change-map', (params: { type: string }) => {
        markers.forEach(marker => marker.setMap(null))
        markers = []

        const bounds = new google.maps.LatLngBounds(),
            salons = Livewire.first().posts as salonData[]

        salons.forEach(salon => {
            if (!salon.lat.length || !salon.lng.length) return

            const marker = createSalonMarker(salon, true)

            markers.push(marker)
            bounds.extend(marker.getPosition())
        })
        
        if (params.type === 'mapy') return;
        removeUserMapListeners();

        if (salons.length > 1) {
            map.fitBounds(bounds, 80)
        } else if (salons.length) {
            map.panTo(getSalonLocation(salons[0]))
            map.setZoom(14)
        }

        setTimeout(() => {
            addUserMapListeners();
        }, 400)
    })

    document.querySelector('.SalonArchiveGrid').addEventListener("mouseover", (e: MouseEvent) => {
        const card = e.target;
        if (card instanceof HTMLElement && card.classList.contains('Salon')) {
            markers.find(marker => marker.salonID === parseInt(card.getAttribute('wire:key')))?.setIcon({
                url: `${map.getDiv().dataset.themeUri}/resources/icons/pin-dark.svg`,
                scaledSize: new google.maps.Size(32, 32),
            })

            card.addEventListener("mouseleave", () => {
                markers.forEach(marker => marker.setIcon({
                    url: `${map.getDiv().dataset.themeUri}/resources/icons/pin.svg`,
                    scaledSize: new google.maps.Size(32, 32),
                }))
            }, { once: true });
        }
    })
}

const getSalonLocation = (salon: salonData) => {
    return { lat: parseFloat(salon.lat), lng: parseFloat(salon.lng) }
}

const createSalonMarker = (salon: salonData, isArchiveMap: boolean = false) => {
    const marker =  new google.maps.Marker({
        position: getSalonLocation(salon),
        clickable: isArchiveMap,
        icon: {
            url: `${map.getDiv().dataset.themeUri}/resources/icons/pin.svg`,
            scaledSize: new google.maps.Size(32, 32),
        },
        map: map
    });

    // @ts-ignore
    marker.salonID = salon.ID;
    isArchiveMap && marker.addListener("click", () => { window.location.href = salon.permalink });

    return marker;
}

const isMapBeyondFirstRender = (mapEl: HTMLElement) => {
    return mapEl.classList.contains('is-loaded')
}

const mapStyle = [
    {
        featureType: "landscape",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }],
    },
    {
        stylers: [{ hue: "#00aaff" }, { saturation: -100 }, { gamma: 2.15 }, { lightness: 12 }],
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ visibility: "on" }, { lightness: 24 }],
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ lightness: 57 }],
    },
]